/**
 * Browser-compatible debug logger utility for tracking chat flow and message handling
 * This helps us track down issues with message content and question handling
 */

// Enable or disable detailed logging
const DEBUG_ENABLED = true;
const LOG_PREFIX = '[CHAT-DEBUG]';

// In-memory log storage
let logEntries: string[] = [];
const MAX_LOG_ENTRIES = 1000;

// Log levels
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Log entry structure
interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  data?: any;
  file?: string;
  line?: number;
}

/**
 * Log a message with context information
 */
export function logWithContext(
  level: LogLevel,
  context: string,
  message: string,
  data?: any,
  file?: string,
  line?: number
) {
  if (!DEBUG_ENABLED) return;

  const timestamp = new Date().toISOString();
  
  // Create log entry object
  const logEntry: LogEntry = {
    timestamp,
    level,
    context,
    message,
    data,
    file,
    line
  };
  
  // Format the log entry for console
  const logString = `${timestamp} ${LOG_PREFIX} [${level.toUpperCase()}] [${context}] ${message}`;
  
  // Log to console
  switch (level) {
    case 'debug':
      console.debug(logString, data || '');
      break;
    case 'info':
      console.info(logString, data || '');
      break;
    case 'warn':
      console.warn(logString, data || '');
      break;
    case 'error':
      console.error(logString, data || '');
      break;
  }
  
  // Store in memory
  logEntries.push(JSON.stringify(logEntry));
  if (logEntries.length > MAX_LOG_ENTRIES) {
    logEntries.shift(); // Remove oldest entry
  }
  
  // Dispatch a custom event for test frameworks to capture
  try {
    const logEvent = new CustomEvent('chat-debug-log', {
      detail: logEntry
    });
    document.dispatchEvent(logEvent);
  } catch (error) {
    console.error('Failed to dispatch log event:', error);
  }
}

/**
 * Debug level logging
 */
export function debugLog(context: string, message: string, data?: any) {
  logWithContext('debug', context, message, data);
}

/**
 * Info level logging
 */
export function infoLog(context: string, message: string, data?: any) {
  logWithContext('info', context, message, data);
}

/**
 * Warning level logging
 */
export function warnLog(context: string, message: string, data?: any) {
  logWithContext('warn', context, message, data);
}

/**
 * Error level logging
 */
export function errorLog(context: string, message: string, data?: any) {
  logWithContext('error', context, message, data);
}

/**
 * Get all stored log entries
 */
export function getLogEntries(): LogEntry[] {
  return logEntries.map(entry => JSON.parse(entry));
}

/**
 * Clear all stored log entries
 */
export function clearLogEntries(): void {
  logEntries = [];
}

/**
 * Export all logs to a string
 */
export function exportLogs(): string {
  return logEntries.join('\n');
}

/**
 * Download logs as a JSON file
 */
export function downloadLogs(): void {
  const blob = new Blob([exportLogs()], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chat-debug-logs-${new Date().toISOString()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default {
  debugLog,
  infoLog,
  warnLog,
  errorLog,
  getLogEntries,
  clearLogEntries,
  exportLogs,
  downloadLogs
};
